import { InstrumentTypeResponse } from "../../../codegen-api";
import { apiNum } from "../../../utils/math";
import { UNKNOWN_DISPLAY } from "../../../utils/unknownNumbers";
import {
  BASIS_POINTS,
  MARKET_ORDER_MARGIN_BUFFER_BPS,
} from "./PerpsTradeForm.helpers";

type FeeRates = {
  maker_fee?: string | number | null;
  taker_fee?: string | number | null;
} | null;

interface FeeStructureLike {
  instrument_type?: string;
  asset?: string | null;
  maker_fee?: string | number | null;
  taker_fee?: string | number | null;
}

/**
 * The fee schedule that applies to a perp order on `asset`: the market's own
 * row, else the perp "ALL" row, else a blanket "ALL" row. Shared so the trade
 * form and the edit-order modal cannot price the same order differently.
 */
export const pickPerpFeeStructure = <T extends FeeStructureLike>(
  structures: T[] | undefined,
  underlyingAsset: string | undefined
): T | undefined => {
  const rows = structures || [];
  const asset = String(underlyingAsset || "").toUpperCase();
  const isPerpFee = (instrumentType?: string) =>
    instrumentType === InstrumentTypeResponse.Perpetual;
  const isAllAsset = (feeAsset?: string | null) =>
    String(feeAsset || "").toUpperCase() === "ALL";

  return (
    rows.find(
      (f) => isPerpFee(f.instrument_type) && String(f.asset || "").toUpperCase() === asset
    ) ||
    rows.find((f) => isPerpFee(f.instrument_type) && isAllAsset(f.asset)) ||
    rows.find((f) => isAllAsset(f.asset))
  );
};

/**
 * The fee rate an opening order may pay: the higher of maker and taker. The
 * backend sends `null` (or no fee structure at all) when no verified schedule
 * exists; that is UNKNOWN, not a free trade. `Number(null)` used to make it 0.
 */
export const resolveOpeningFeeRate = (
  fee?: FeeRates
): number | undefined => {
  const maker = apiNum(fee?.maker_fee);
  const taker = apiNum(fee?.taker_fee);
  return maker !== undefined && taker !== undefined
    ? Math.max(maker, taker)
    : undefined;
};

/** "taker% / maker%", or "-" when either rate is unknown (was "0.0000%"). */
export const formatFeeRates = (fee?: FeeRates): string => {
  const maker = apiNum(fee?.maker_fee);
  const taker = apiNum(fee?.taker_fee);
  if (maker === undefined || taker === undefined) return UNKNOWN_DISPLAY;
  return `${(taker * 100).toFixed(4)}% / ${(maker * 100).toFixed(4)}%`;
};

/**
 * How much notional the account can open. 0 is this form's "unknown" (shown
 * as "---", and it blocks sizing a new exposure while reduce-only closes stay
 * allowed). It is unknown when the backend cannot value the account
 * (`withdrawable: null`) or when the fee rate is unknown: assuming a 0% fee
 * would over-state buying power.
 */
export const computeBuyingPower = (
  account:
    | {
        withdrawable?: string | number | null;
        available_balance?: string | number | null;
      }
    | undefined,
  selectedLeverage: number,
  openingFeeRate: number | undefined,
  isMarketOrder: boolean
): number => {
  if (!account) return 0;
  if ((account.withdrawable as unknown) === null) return 0;
  if (openingFeeRate === undefined) return 0;
  // Spendable base = free equity after margin (withdrawable). Falls back to
  // available_balance when the API omits it.
  const availableBalance =
    apiNum(account.withdrawable) ?? (Number(account.available_balance) || 0);
  const marginAndFeeRate = 1 / selectedLeverage + openingFeeRate;
  const rawBuyingPower =
    marginAndFeeRate > 0 ? availableBalance / marginAndFeeRate : 0;
  return isMarketOrder
    ? (rawBuyingPower * (BASIS_POINTS - MARKET_ORDER_MARGIN_BUFFER_BPS)) /
        BASIS_POINTS
    : rawBuyingPower;
};
